import React, { useState, useEffect } from "react";
import { Card, Button, Row, Col, Badge, Image, Modal, Form, Spinner } from "react-bootstrap";
import { FaEdit, FaTrash, FaSync, FaShareAlt, FaCertificate, FaMapMarkerAlt, FaBriefcase, FaCalendarAlt, FaEye, FaUpload, FaCheckCircle } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import axios from 'axios';
import Swal from 'sweetalert2';
// import defaultProfileImage from "../../assets/images/default-profile.jpg";
import BaseUrl from "../../ApiServices/BaseUrl";
import loadRazorpay from "../../ApiServices/loadRazorpay";
import "./ProfileCard.css";
import { getVisibleOfferBadgeText, getVisibleSourceLabel, hasHighlightedSourceLabel, hasVisibleOfferBadge, formatApplyForCountry } from "../../../utils/candidateBadges";

const ProfileCard = () => {
    const [profile, setProfile] = useState(null);
    const [currentSub, setCurrentSub] = useState(null);
    const [currentFeatures, setCurrentFeatures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [reposting, setReposting] = useState(false);
    const navigate = useNavigate();

    // Publishing states
    const [showPublishModal, setShowPublishModal] = useState(false);
    const [publishFeeInfo, setPublishFeeInfo] = useState({ enabled: false, fee: 0, currency: 'AED' });
    const [walletBalance, setWalletBalance] = useState(0);
    const [isPublishing, setIsPublishing] = useState(false);

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const userData = JSON.parse(localStorage.getItem('user'));
                const userId = userData?.id || localStorage.getItem('userId');

                if (!userId) {
                    throw new Error('User identification not found in localStorage');
                }

                const response = await fetch(`${BaseUrl}/api/job-seeker/${userId}`);
                if (!response.ok) {
                    throw new Error('Failed to fetch profile data');
                }
                const data = await response.json();

                if (data.profile_photo) {
                    data.profile_photo = `${BaseUrl}${data.profile_photo}`;
                }

                setProfile(data);

                const subResponse = await fetch(`${BaseUrl}/api/candidate/subscription/${userId}`);
                if (subResponse.ok) {
                    const subData = await subResponse.json();
                    setCurrentSub(subData.subscription || null);
                    setCurrentFeatures(subData.features || []);
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchProfile();
    }, []);

    // Helper function to format salary
    const formatSalary = () => {
        if (!profile) return "Not specified";

        const rawLocalSalary = String(profile.expected_monthly_salary || "").trim();
        const rawUsdSalary = String(profile.expected_monthly_salary_usd || "").trim();
        const salaryCurrency = String(profile.expected_monthly_salary_currency || "").trim();
        const isNegotiable =
            profile.salary_negotiable === true ||
            rawLocalSalary.toLowerCase() === "negotiable" ||
            rawUsdSalary.toLowerCase() === "negotiable";

        if (isNegotiable) {
            return "Negotiable";
        }

        const usdAmount = parseFloat(rawUsdSalary);
        if (!Number.isNaN(usdAmount) && usdAmount > 0) {
            const dhsAmount = Math.round(usdAmount * 3.67);
            return `${Math.round(usdAmount)} USD / ${dhsAmount} DHS`;
        }

        const localAmount = parseFloat(rawLocalSalary);
        if (!Number.isNaN(localAmount) && localAmount > 0) {
            const currencyCode = salaryCurrency ? salaryCurrency.split(" ")[0] : "";
            return currencyCode
                ? `${Math.round(localAmount)} ${currencyCode}`
                : `${Math.round(localAmount)}`;
        }

        return "Negotiable";
    };

    // Helper function to format date
    // Helper function to format date or handle text
    const formatDate = (dateString) => {
        if (!dateString) return 'Not specified';

        // If it's a text string like "Immediate", return it as-is
        if (dateString.toLowerCase() === "immediate" ||
            dateString.toLowerCase() === "asap" ||
            dateString.toLowerCase() === "anytime") {
            return dateString;
        }

        // Try to parse it as a date
        const date = new Date(dateString);

        // Check if the date is valid
        if (isNaN(date.getTime())) {
            // If not a valid date, return the original string
            return dateString;
        }

        // Format the valid date
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };
    // Helper function to get first language
    const getMainLanguage = () => {
        if (!profile?.language) return 'Not specified';

        try {
            const languages = typeof profile.language === 'string'
                ? JSON.parse(profile.language)
                : profile.language;

            if (Array.isArray(languages) && languages.length > 0) {
                const firstLang = languages[0];
                return typeof firstLang === 'object' && firstLang.language
                    ? firstLang.language
                    : firstLang;
            }
            return 'Not specified';
        } catch (e) {
            return 'Not specified';
        }
    };

    // Helper function to get main skills
    const getMainSkills = () => {
        if (!profile?.main_skills) return 'Not specified';

        try {
            const skills = typeof profile.main_skills === 'string'
                ? JSON.parse(profile.main_skills)
                : profile.main_skills;

            if (Array.isArray(skills) && skills.length > 0) {
                const firstSkill = skills[0];
                if (typeof firstSkill === 'string' && firstSkill.includes('["')) {
                    try {
                        const parsed = JSON.parse(firstSkill.replace(/\\/g, ''));
                        if (Array.isArray(parsed)) {
                            return parsed.join(', ');
                        }
                        return parsed;
                    } catch (e) {
                        return firstSkill;
                    }
                }
                return firstSkill;
            }
            return 'Not specified';
        } catch (e) {
            return 'Not specified';
        }
    };

    // Check if profile contains specific job positions
    const isNurse = () => {
        if (!profile?.job_position) return false;
        const positions = Array.isArray(profile.job_position)
            ? profile.job_position
            : [profile.job_position];
        return positions.some(pos =>
            typeof pos === 'string' && pos.toLowerCase().includes("nurse")
        );
    };

    const isDoctor = () => {
        if (!profile?.job_position) return false;
        const positions = Array.isArray(profile.job_position)
            ? profile.job_position
            : [profile.job_position];
        return positions.some(pos =>
            typeof pos === 'string' && pos.toLowerCase().includes("doctor")
        );
    };

    const isDriver = () => {
        if (!profile?.job_position) return false;
        const positions = Array.isArray(profile.job_position)
            ? profile.job_position
            : [profile.job_position];
        return positions.some(pos =>
            typeof pos === 'string' && pos.toLowerCase().includes("driver")
        );
    };

    // Publishing handlers
    const fetchPublishInfo = async () => {
        try {
            const userData = JSON.parse(localStorage.getItem('user'));
            const userId = userData?.id || localStorage.getItem('userId');
            if (!userId) return;

            console.log('Fetching publishing fee from:', `${BaseUrl}/api/job-seeker/publish-fee`);
            const feeRes = await axios.get(`${BaseUrl}/api/job-seeker/publish-fee`);
            console.log('Publish fee response:', feeRes.data);

            if (feeRes.data && typeof feeRes.data === 'object') {
                setPublishFeeInfo(feeRes.data);
            }

            const walletRes = await axios.get(`${BaseUrl}/api/candidate/wallet/${userId}`);
            setWalletBalance(walletRes.data?.balance || 0);
        } catch (err) {
            console.error('Error fetching publish info/wallet:', err);
        }
    };

    const handleOpenPublishModal = () => {
        fetchPublishInfo();
        setShowPublishModal(true);
    };

    const handlePublishWallet = async () => {
        setIsPublishing(true);
        try {
            const userData = JSON.parse(localStorage.getItem('user'));
            const userId = userData?.id || localStorage.getItem('userId');
            const res = await axios.post(`${BaseUrl}/api/job-seeker/publish/wallet`, { candidate_id: userId });

            Swal.fire('Success', res.data.message, 'success');
            setShowPublishModal(false);
            window.location.reload();
        } catch (err) {
            Swal.fire('Error', err.response?.data?.error || err.message, 'error');
        } finally {
            setIsPublishing(false);
        }
    };

    const handlePublishRazorpay = async () => {
        setIsPublishing(true);
        try {
            const userData = JSON.parse(localStorage.getItem('user'));
            const userId = userData?.id || localStorage.getItem('userId');

            const options = {
                key: process.env.REACT_APP_RAZORPAY_KEY,
                amount: publishFeeInfo.fee * 100,
                currency: publishFeeInfo?.currency || "AED",
                name: "Profile Publishing",
                description: "One-time profile publishing fee",
                handler: async function (response) {
                    try {
                        const verifyRes = await axios.post(`${BaseUrl}/api/job-seeker/publish/razorpay-success`, {
                            candidate_id: userId,
                            razorpay_payment_id: response.razorpay_payment_id
                        });
                        Swal.fire('Success', verifyRes.data.message, 'success');
                        setShowPublishModal(false);
                        window.location.reload();
                    } catch (verifyErr) {
                        Swal.fire('Error', 'Payment verification failed', 'error');
                    }
                },
                prefill: {
                    name: profile?.first_name || '',
                    email: profile?.email_id || '',
                    contact: profile?.whatsapp_number || ''
                },
                theme: { color: "#004a8c" }
            };
            const isRazorpayReady = await loadRazorpay();
            if (!isRazorpayReady) {
                Swal.fire('Error', 'Payment gateway failed to load. Please try again.', 'error');
                return;
            }
            const rzp1 = new window.Razorpay(options);
            rzp1.on('payment.failed', function (response) {
                Swal.fire('Error', response.error.description, 'error');
            });
            rzp1.open();
        } catch (err) {
            console.error('Razorpay Error:', err);
            Swal.fire('Error', 'Could not initialize payment', 'error');
        } finally {
            setIsPublishing(false);
        }
    };

    // Button handlers
    const handleViewProfile = () => {
        const userData = JSON.parse(localStorage.getItem('user'));
        const userId = userData?.id || localStorage.getItem('userId');

        if (userId) {
            navigate(`/allcandidatesdetails/${userId}`);
        } else {
            console.error("User ID not found for navigation");
        }
    };

    const handleEditProfile = () => {
        navigate('/personalinfo');
    };

    const handleUploadPhoto = () => {
        const userData = JSON.parse(localStorage.getItem('user'));
        const userId = userData?.id || localStorage.getItem('userId');
        navigate('/choosephoto', { state: { userId, fromProfile: true } });
    };

    const hasPremiumRepostAccess = () => {
        return currentFeatures.some(feature =>
            feature.feature_key === 'candidate_repost_access' && feature.is_enabled
        );
    };

    const getRepostCooldownEnd = () => {
        const lastRepostedAt = profile?.last_reposted_at || profile?.reposted_at;
        if (!lastRepostedAt) return null;

        const nextAvailableAt = new Date(lastRepostedAt);
        if (Number.isNaN(nextAvailableAt.getTime())) return null;

        nextAvailableAt.setDate(nextAvailableAt.getDate() + 7);
        return nextAvailableAt;
    };

    const isInRepostCooldown = () => {
        const nextAvailableAt = getRepostCooldownEnd();
        return !!nextAvailableAt && nextAvailableAt > new Date();
    };

    const handleRepostProfile = async () => {
        const userData = JSON.parse(localStorage.getItem('user'));
        const userId = userData?.id || localStorage.getItem('userId');

        if (!userId) {
            Swal.fire('Error', 'User not found. Please login again.', 'error');
            return;
        }

        if (!hasPremiumRepostAccess()) {
            Swal.fire({
                icon: 'info',
                title: 'Upgrade Required',
                text: 'Repost is not enabled in your current plan.',
                confirmButtonText: 'View Plans'
            }).then((result) => {
                if (result.isConfirmed) {
                    navigate('/candidate-subscription');
                }
            });
            return;
        }

        const nextAvailableAt = getRepostCooldownEnd();
        if (nextAvailableAt && nextAvailableAt > new Date()) {
            Swal.fire(
                'Error',
                `This profile is already reposted. It can be reposted again after ${nextAvailableAt.toLocaleString('en-IN')}.`,
                'error'
            );
            return;
        }

        setReposting(true);
        try {
            const response = await axios.post(`${BaseUrl}/api/job-seeker/${userId}/repost`, {
                requester_id: userId,
                requester_role: 'candidate'
            });

            const repostedAt = response.data?.candidate?.reposted_at;
            setProfile(prev => prev ? {
                ...prev,
                reposted_at: repostedAt,
                last_reposted_at: response.data?.candidate?.last_reposted_at
            } : prev);

            Swal.fire('Success', 'Profile reposted successfully.', 'success');
        } catch (err) {
            const message = err.response?.data?.next_available_at
                ? `This profile is already reposted. It can be reposted again after ${new Date(err.response.data.next_available_at).toLocaleString('en-IN')}.`
                : err.response?.data?.error || 'Failed to repost profile';
            Swal.fire('Error', message, 'error');
        } finally {
            setReposting(false);
        }
    };

    const handleDeleteProfile = () => {
        if (window.confirm('Are you sure you want to delete your profile? This action cannot be undone.')) {
            alert('Delete Profile functionality to be implemented');
            // Add your delete logic here
        }
    };

    if (loading) {
        return (
            <div className="profile-loading">
                <div className="spinner-border text-success" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2 text-muted">Loading profile...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="profile-empty-state">
                <div className="card shadow-sm border-0">
                    <div className="card-body text-center p-4 p-md-5">
                        <h3 className="fw-bold text-dark mb-3">Complete Your Profile</h3>
                        <p className="text-muted mb-4">
                            Your profile information is incomplete or there was an error loading it.
                        </p>
                        <div className="update-resume-section mb-4">
                            <button
                                className="btn btn-outline-primary btn-lg px-4"
                                onClick={() => navigate('/personalinfo')}
                            >
                                <i className="bi bi-file-earmark-pdf me-2"></i>
                                Upload Resume
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!profile) {
        return (
            <div className="profile-not-found">
                <div className="text-warning fs-1 mb-2">👤</div>
                <p className="text-muted">No profile found</p>
            </div>
        );
    }

    const jobPositions = Array.isArray(profile.job_position)
        ? profile.job_position.join(', ')
        : profile.job_position || 'Not specified';

    const hasPurchasedBadge = Number(profile.purchased_badge) === 1;
    const hasPurchasedTag = hasVisibleOfferBadge(profile);
    const hasBothAddOns = hasHighlightedSourceLabel(profile);



    return (
        <>
            <Card className="profile-card-modern mb-1" style={{ border: 'none' }}>
                <Card.Body className="p-3">
                    <div className="d-flex">
                        <div className="me-3 text-center">
                            {/* Labels (Verified / New) */}
                            <div className="d-flex justify-content-center gap-2 mt-2">
                                {hasPurchasedBadge && (
                                    <div className="verified-status-label">
                                        <FaCheckCircle className="text-success me-1" />
                                        <span>Verified</span>
                                    </div>
                                )}
                            </div>

                             {/* Profile Image */}
                            <div className="position-relative d-flex justify-content-center mt-2 profile-img-container">
                                <div 
                                    className="rounded-circle overflow-hidden border position-relative" 
                                    style={{ width: '100px', height: '100px' }}
                                >
                                    {profile.profile_photo ? (
                                        <Image
                                            src={profile.profile_photo}
                                            className="w-100 h-100"
                                            alt={`${profile.first_name || 'Candidate'} profile`}
                                            style={{ objectFit: 'cover' }}
                                        />
                                    ) : (
                                        <div className="w-100 h-100 bg-light d-flex align-items-center justify-content-center">
                                            {/* (Missing photo handled by container) */}
                                        </div>
                                    )}

                                    {/* Offer Badge (the green bar on photo bottom) */}
                                    {hasPurchasedTag && (
                                        <div className="offer-badge-on-photo">
                                            {getVisibleOfferBadgeText(profile)}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div 
                                className="very-active-label" 
                                style={{ 
                                    color: hasBothAddOns ? '#28a745' : '#6c757d', 
                                    fontWeight: hasBothAddOns ? 'bold' : 'normal',
                                    marginTop: '8px'
                                }}
                            >
                                {getVisibleSourceLabel(profile)}
                            </div>
                        </div>

                        <div className="flex-grow-1 allcandidate-card-right">
                            {isNurse() ? (
                                // Nurse layout
                                <>
                                    <h6 className="fw-bold allcandidates-name-heading">
                                        {profile.first_name
                                            ? profile.first_name.split("@")[0].charAt(0).toUpperCase() + profile.first_name.split("@")[0].slice(1)
                                            : ""} | {profile.age} years
                                    </h6>
                                    <ul className="small ps-3 mb-0 domesticcustom-list domesticcandidate-normal-details">
                                        <li>
                                            <strong>Job Position - </strong>
                                            {jobPositions}
                                        </li>
                                        <li>
                                            <strong>Salary - </strong>
                                            {formatSalary()}
                                        </li>
                                        <li>
                                            <strong>Experience -</strong>{' '}
                                            {profile.total_work_experience || 'Not specified'}
                                        </li>
                                        <li>
                                            <strong>Language -</strong> {getMainLanguage()}
                                        </li>
                                        <li>
                                            <strong>Country -</strong> <span>{profile.nationality || 'Not specified'}</span>
                                        </li>
                                        <li>
                                            <strong>Current Location -</strong>{' '}
                                            <span>{profile.current_country || 'Not specified'}</span>
                                        </li>
                                        <li>
                                            <strong>Apply for -</strong>{' '}
                                            <span>{formatApplyForCountry(profile.country_apply_for_job)}</span>
                                        </li>
                                        <li>
                                            <strong>Accommodation - </strong>{' '}
                                            <span>{profile.accommodation_preference || 'Not specified'}</span>
                                        </li>
                                        <li>
                                            <strong>Visa Status - </strong>{' '}
                                            <span>{profile.visa_type || 'Not specified'}</span>
                                        </li>
                                        <li>
                                            <strong>Available From - </strong>{' '}
                                            <span>
                                                {profile.preferred_start_date
                                                    ? formatDate(profile.preferred_start_date)
                                                    : 'Not specified'
                                                }
                                            </span>
                                        </li>
                                    </ul>
                                </>
                            ) : isDoctor() ? (
                                // Doctor layout
                                <>
                                    <h6 className="fw-bold allcandidates-name-heading">
                                        Dr. {profile.first_name
                                            ? profile.first_name.split("@")[0].charAt(0).toUpperCase() + profile.first_name.split("@")[0].slice(1)
                                            : ""} | {profile.age} years
                                    </h6>
                                    <ul className="small ps-3 mb-0 domesticcustom-list domesticcandidate-normal-details">
                                        <li>
                                            <strong>Job Position - </strong>
                                            {jobPositions}
                                        </li>
                                        <li>
                                            <strong>Salary - </strong>
                                            {formatSalary()}
                                        </li>
                                        <li>
                                            <strong>Experience -</strong>{' '}
                                            {profile.total_work_experience || 'Not specified'}
                                        </li>
                                        <li>
                                            <strong>Language -</strong> {getMainLanguage()}
                                        </li>
                                        <li>
                                            <strong>Country -</strong> <span>{profile.nationality || 'Not specified'}</span>
                                        </li>
                                        <li>
                                            <strong>Current Location -</strong>{' '}
                                            <span>{profile.current_country || 'Not specified'}</span>
                                        </li>
                                        <li>
                                            <strong>Apply for -</strong>{' '}
                                            <span>{formatApplyForCountry(profile.country_apply_for_job)}</span>
                                        </li>
                                        <li>
                                            <strong>Accommodation - </strong>{' '}
                                            <span>{profile.accommodation_preference || 'Not specified'}</span>
                                        </li>
                                        <li>
                                            <strong>Visa Status - </strong>{' '}
                                            <span>{profile.visa_type || 'Not specified'}</span>
                                        </li>
                                        <li>
                                            <strong>Available From - </strong>{' '}
                                            <span>
                                                {profile.preferred_start_date
                                                    ? formatDate(profile.preferred_start_date)
                                                    : 'Not specified'
                                                }
                                            </span>
                                        </li>
                                    </ul>
                                </>
                            ) : isDriver() ? (
                                // Driver layout
                                <>
                                    <h6 className="fw-bold allcandidates-name-heading">
                                        {profile.first_name
                                            ? profile.first_name.split("@")[0].charAt(0).toUpperCase() + profile.first_name.split("@")[0].slice(1)
                                            : ""} - {profile.age} years
                                    </h6>
                                    <ul className="small ps-3 mb-0 domesticcustom-list domesticcandidate-normal-details">
                                        <li>
                                            <strong>Job Position - </strong>
                                            {jobPositions}
                                        </li>
                                        <li>
                                            <strong>Salary - </strong>
                                            {formatSalary()}
                                        </li>
                                        <li>
                                            <strong>Experience -</strong>{' '}
                                            {profile.total_work_experience || 'Not specified'}
                                        </li>
                                        <li>
                                            <strong>Language -</strong> {getMainLanguage()}
                                        </li>
                                        <li>
                                            <strong>Country -</strong> <span>{profile.nationality || 'Not specified'}</span>
                                        </li>
                                        <li>
                                            <strong>Current Location -</strong>{' '}
                                            <span>{profile.current_country || 'Not specified'}</span>
                                        </li>
                                        <li>
                                            <strong>Apply for -</strong>{' '}
                                            <span>{formatApplyForCountry(profile.country_apply_for_job)}</span>
                                        </li>
                                        <li>
                                            <strong>Accommodation - </strong>{' '}
                                            <span>{profile.accommodation_preference || 'Not specified'}</span>
                                        </li>
                                        <li>
                                            <strong>Visa Status - </strong>{' '}
                                            <span>{profile.visa_type || 'Not specified'}</span>
                                        </li>
                                        <li>
                                            <strong>Available From - </strong>{' '}
                                            <span>
                                                {profile.preferred_start_date
                                                    ? formatDate(profile.preferred_start_date)
                                                    : 'Not specified'
                                                }
                                            </span>
                                        </li>
                                    </ul>
                                </>
                            ) : (
                                // Default layout
                                <>
                                    <h6 className="fw-bold allcandidates-name-heading-nrml">
                                        {profile.first_name?.split("@")[0] || 'User'} - {profile.age || 'N/A'} years
                                    </h6>
                                    <ul className="small ps-3 mb-0 allcandidatescustom-list allcandidate-normal-details">
                                        <li>
                                            <strong>Job Position - </strong>
                                            {jobPositions}
                                        </li>
                                        <li>
                                            <strong>Salary - </strong>
                                            {formatSalary()}
                                        </li>
                                        <li>
                                            <strong>Experience -</strong>{' '}
                                            {profile.total_work_experience || "Not specified"}
                                        </li>
                                        <li>
                                            <strong>Language -</strong> {getMainLanguage()}
                                        </li>
                                        <li>
                                            <strong>Country -</strong>{' '}
                                            <span>{profile.nationality || "Not specified"}</span>
                                        </li>
                                        <li>
                                            <strong>Current Location -</strong>{' '}
                                            <span>{profile.current_country || "Not specified"}</span>
                                        </li>
                                        <li>
                                            <strong>Apply for -</strong>{' '}
                                            <span>{formatApplyForCountry(profile.country_apply_for_job)}</span>
                                        </li>
                                        <li>
                                            <strong>Accommodation - </strong>{' '}
                                            <span>{profile.accommodation_preference || 'Not specified'}</span>
                                        </li>
                                        <li>
                                            <strong>Visa Status -</strong>{' '}
                                            <span>{profile.visa_type || 'Not specified'}</span>
                                        </li>
                                        <li>
                                            <strong>Available From -</strong>{' '}
                                            <span>
                                                {profile.preferred_start_date
                                                    ? formatDate(profile.preferred_start_date)
                                                    : 'Not specified'
                                                }
                                            </span>
                                        </li>
                                    </ul>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Publishing Banner if Unpublished */}
                    {profile.verification_status?.toLowerCase() === 'unpublished' && (
                        <div className="mb-2 mt-2 px-1">
                            <div className="d-flex align-items-center justify-content-between p-2 border-0 shadow-sm"
                                style={{
                                    borderRadius: '8px',
                                    background: 'linear-gradient(135deg, #fff9e6 0%, #fff3cd 100%)',
                                    borderLeft: '4px solid #ffc107'
                                }}>
                                <div className="d-flex align-items-center">
                                    <div className="bg-warning bg-opacity-25 rounded-circle p-1 me-2 d-flex align-items-center justify-content-center">
                                        <i className="bi bi-megaphone-fill text-warning" style={{ fontSize: '0.9rem' }}></i>
                                    </div>
                                    <div>
                                        <div className="fw-bold text-dark" style={{ fontSize: '0.85rem' }}>Profile Unpublished</div>
                                        <div className="text-muted" style={{ fontSize: '0.7rem' }}>Your profile isn't visible to employers yet.</div>
                                    </div>
                                </div>
                                <Button
                                    variant="success"
                                    className="fw-bold rounded-pill shadow-sm"
                                    onClick={handleOpenPublishModal}
                                    style={{ color: '#ffffff', border: 'none', fontSize: '0.75rem', padding: '0.35rem 0.75rem' }}
                                >
                                    <FaUpload className="me-1" style={{ fontSize: '0.7rem' }} /> Publish Now
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="profile-action-row mt-1">
                        <div className="profile-action-col">
                            <Button
                                variant=""
                                className="w-100 small-btn action-button profile-action-btn" style={{ borderRadius: '8px', backgroundColor: '#3d6e6e', color: 'white' }}
                                onClick={handleViewProfile}
                            >
                                <FaEye className="profile-action-icon me-2" />
                                <span className="action-label">View</span>
                            </Button>
                        </div>
                        <div className="profile-action-col">
                            <Button
                                variant=""
                                className="w-100 small-btn action-button gap-2 profile-action-btn"
                                style={{ borderRadius: '8px', backgroundColor: '#6f42c1', border: '#6f42c1', color: 'white' }}
                                onClick={handleUploadPhoto}
                            >
                                <FaUpload className="profile-action-icon" />
                                <span className="action-label">Photo</span>
                            </Button>
                        </div>
                        <div className="profile-action-col">
                            <Button
                                variant=""
                                className="w-100 small-btn action-button gap-2 profile-action-btn" style={{ borderRadius: '8px', backgroundColor: 'blue', border: 'blue', color: 'white' }}
                                onClick={handleEditProfile}
                            >
                                <FaEdit className="profile-action-icon" />
                                <span className="action-label">Edit</span>
                            </Button>
                        </div>
                        <div className="profile-action-col">
                            <Button
                                variant="info"
                                className="w-100 small-btn action-button gap-2 profile-action-btn"
                                style={{
                                    borderRadius: '8px',
                                    opacity: hasPremiumRepostAccess() && !isInRepostCooldown() ? 1 : 0.55,
                                    backgroundColor: hasPremiumRepostAccess() && !isInRepostCooldown() ? undefined : '#b9dfea',
                                    borderColor: hasPremiumRepostAccess() && !isInRepostCooldown() ? undefined : '#b9dfea',
                                    color: hasPremiumRepostAccess() && !isInRepostCooldown() ? undefined : '#2c5964'
                                }}
                                onClick={handleRepostProfile}
                                disabled={reposting}
                            >
                                <FaSync className="profile-action-icon" />
                                <span className="action-label">{reposting ? 'Reposting...' : isInRepostCooldown() ? 'Reposted' : 'Repost'}</span>
                            </Button>
                        </div>
                        <div className="profile-action-col">
                            <Button
                                variant=""
                                className="w-100 small-btn action-button profile-action-btn" style={{ borderRadius: '8px', backgroundColor: 'red', color: 'white' }}
                                onClick={handleDeleteProfile}
                            >
                                <FaTrash className="profile-action-icon" />
                                <span className="action-label">Delete</span>
                            </Button>
                        </div>
                    </div>
                </Card.Body>
            </Card>

            {/* Publishing Payment Modal */}
            <Modal
                show={showPublishModal}
                onHide={() => !isPublishing && setShowPublishModal(false)}
                centered
                size="md"
                dialogClassName="custom-modal-width"
                className="publish-modal-custom"
                backdrop="static"
            >
                <Modal.Body className="p-0 overflow-hidden" style={{ borderRadius: '16px' }}>
                    <div className="d-flex flex-column flex-md-row">
                        {/* Left Side: Info */}
                        <div className="p-4 p-md-4 flex-grow-1" style={{ backgroundColor: '#ffffff', position: 'relative' }}>
                            <h5 className="fw-bold mb-3" style={{ color: '#2b3940', fontSize: '1.25rem' }}>One-Time Profile Publishing</h5>
                            <p className="mb-4" style={{ fontSize: '0.9rem', color: '#4a5568', lineHeight: '1.5' }}>
                                Unlock your profile for employers and start receiving <strong>direct job offers</strong>, <strong style={{ color: '#2b3940' }}>WhatsApp Messages &amp; Calls Daily.</strong>
                            </p>

                            <ul className="list-unstyled mb-4" style={{ fontSize: '0.85rem' }}>
                                <li className="d-flex align-items-center mb-2">
                                    <i className="bi bi-check-circle-fill me-2" style={{ color: '#278b5e', fontSize: '1.1rem' }}></i>
                                    <span style={{ color: '#2b3940', fontWeight: '500' }}>Profile visible to all <strong>verified</strong> employers</span>
                                </li>
                                <li className="d-flex align-items-center mb-2">
                                    <i className="bi bi-check-circle-fill me-2" style={{ color: '#278b5e', fontSize: '1.1rem' }}></i>
                                    <span style={{ color: '#2b3940', fontWeight: '500' }}>Higher ranking in employer search results</span>
                                </li>
                                <li className="d-flex align-items-center mb-2">
                                    <i className="bi bi-check-circle-fill me-2" style={{ color: '#278b5e', fontSize: '1.1rem' }}></i>
                                    <span style={{ color: '#2b3940', fontWeight: '500' }}><strong>Direct WhatsApp &amp; Call</strong> enquiries</span>
                                </li>
                                <li className="d-flex align-items-center mb-2">
                                    <i className="bi bi-check-circle-fill me-2" style={{ color: '#278b5e', fontSize: '1.1rem' }}></i>
                                    <span style={{ color: '#2b3940', fontWeight: '500' }}>Verified badge after admin approval</span>
                                </li>
                                <li className="d-flex align-items-center mb-2">
                                    <i className="bi bi-check-circle-fill me-2" style={{ color: '#278b5e', fontSize: '1.1rem' }}></i>
                                    <span style={{ color: '#2b3940', fontWeight: '500' }}>Priority exposure for <strong>Gulf &amp; Europe jobs</strong></span>
                                </li>
                                <li className="d-flex align-items-center mb-2">
                                    <i className="bi bi-check-circle-fill me-2" style={{ color: '#278b5e', fontSize: '1.1rem' }}></i>
                                    <span style={{ color: '#2b3940', fontWeight: '500' }}>Faster <strong>shortlisting</strong> chances</span>
                                </li>
                                <li className="d-flex align-items-center mb-2">
                                    <i className="bi bi-check-circle-fill me-2" style={{ color: '#278b5e', fontSize: '1.1rem' }}></i>
                                    <span style={{ color: '#2b3940', fontWeight: '500' }}>One-time activation (No monthly fee)</span>
                                </li>
                            </ul>

                            <hr style={{ borderColor: '#e2e8f0', margin: '1rem 0' }} />

                            <div className="d-flex align-items-center mb-1">
                                <i className="bi bi-lock-fill text-secondary me-2" style={{ fontSize: '0.9rem' }}></i>
                                <span style={{ color: '#4a5568', fontSize: '0.8rem' }}><strong>Secure &amp; Safe</strong> - Visible only to verified employers</span>
                            </div>
                            <div className="d-flex align-items-center">
                                <i className="bi bi-info-circle text-secondary me-2" style={{ fontSize: '0.9rem' }}></i>
                                <span style={{ color: '#4a5568', fontSize: '0.8rem' }}>Thousands of employers are searching daily - <strong className="text-dark">activate now</strong></span>
                            </div>
                        </div>

                        {/* Right Side: Price Tag and Buttons */}
                        <div className="d-flex flex-column" style={{ width: '100%', maxWidth: '340px', backgroundColor: '#f8fafc' }}>
                            {/* Green Texture Box */}
                            <div className="p-4 flex-grow-1" style={{
                                backgroundColor: '#4a7c59',
                                backgroundImage: `radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)`,
                                backgroundSize: '10px 10px',
                                backgroundPosition: 'center'
                            }}>
                                <div className="px-3 py-2 rounded mb-4 shadow-sm text-center fw-bold d-flex align-items-center justify-content-center" style={{ fontSize: '0.75rem', backgroundColor: '#fef3c7', color: '#92400e' }}>
                                    <i className="bi bi-star-fill text-warning me-2" style={{ fontSize: '1rem' }}></i>
                                    Most candidates receive first call within 48 hours
                                </div>
                                
                                <div className="text-center text-white mb-4 rounded p-3" style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                                    <div className="h2 fw-bold mb-1">{publishFeeInfo?.currency || 'AED'} {publishFeeInfo?.fee || 1000}</div>
                                    <div className="small opacity-75 mb-3" style={{ fontSize: '0.75rem' }}>One-Time Activation Fee</div>
                                    
                                    <div className="d-flex flex-column align-items-start gap-2 ps-2 pe-1 mt-3 text-start">
                                        <div className="d-flex align-items-center"><i className="bi bi-check2 text-white me-2"></i> <span className="small" style={{ fontSize: '0.8rem' }}>Lifetime visibility</span></div>
                                        <div className="d-flex align-items-center"><i className="bi bi-check2 text-white me-2"></i> <span className="small" style={{ fontSize: '0.8rem' }}>No hidden charges</span></div>
                                        <div className="d-flex align-items-center"><i className="bi bi-check2 text-white me-2"></i> <span className="small" style={{ fontSize: '0.8rem' }}>No renewal required</span></div>
                                    </div>

                                    <div className="mt-3 pt-2 border-top border-white border-opacity-25 d-flex justify-content-center align-items-center">
                                        <i className="bi bi-shield-lock-fill text-warning me-2" style={{ fontSize: '0.85rem' }}></i>
                                        <span className="fw-medium" style={{ fontSize: '0.75rem' }}>100% Secure Checkout</span>
                                    </div>
                                </div>
                            </div>

                            {/* Bottom Actions Area (White) */}
                            <div className="bg-white p-3" style={{ borderTop: '1px solid #e2e8f0', borderBottomRightRadius: '16px' }}>
                                {isPublishing ? (
                                    <div className="text-center py-2">
                                        <Spinner animation="border" size="sm" className="me-2 text-success" />
                                        <span className="fw-bold text-success" style={{ fontSize: '0.85rem' }}>Processing...</span>
                                    </div>
                                ) : (
                                    <>
                                        <div className="d-flex gap-2 mb-2">
                                            <Button 
                                                variant="outline-secondary" 
                                                className="flex-grow-1 fw-bold"
                                                style={{ borderRadius: '6px', fontSize: '0.85rem', padding: '0.4rem' }}
                                                onClick={() => {
                                                    if (walletBalance >= (publishFeeInfo?.fee || 1000)) {
                                                        handlePublishWallet();
                                                    } else {
                                                        setShowPublishModal(false);
                                                    }
                                                }}
                                            >
                                                {walletBalance >= (publishFeeInfo?.fee || 1000) ? 'Pay with Wallet' : 'Cancel'}
                                            </Button>
                                            <Button 
                                                variant="success" 
                                                className="flex-grow-1 fw-bold text-white shadow-sm"
                                                style={{ backgroundColor: '#278b5e', borderColor: '#278b5e', borderRadius: '6px', fontSize: '0.85rem', padding: '0.4rem' }}
                                                onClick={handlePublishRazorpay}
                                            >
                                                Get Job Calls Now
                                            </Button>
                                        </div>
                                        <div className="text-center mt-1">
                                            <span 
                                                className="text-muted" 
                                                style={{ cursor: 'pointer', fontSize: '0.75rem' }} 
                                                onClick={() => setShowPublishModal(false)}
                                            >
                                                or Post Free Now (Limited Visibility) <i className="bi bi-arrow-right"></i>
                                            </span>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
};

export default ProfileCard;
